import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { createAuthCookieStorage } from "../storage/auth-cookie-storage";

export interface User {
  id: string;
  username: string;
  email: string;
  token: string;
  refreshToken: string;
  expiresAt: Date;
  refreshTokenExpiresAt: Date;
  permissions: string[];
  roles: string[];
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  isTokenExpired: () => boolean;
  isRefreshTokenExpired: () => boolean;
  clearAuth: () => void;
}

const STORE_NAME = "CE_auth";

export const createAuthStore = () => {
  const cookieStorage = createAuthCookieStorage();

  return create<AuthState>()(
    persist(
      (set, get) => ({
        user: null,
        isAuthenticated: false,
        setUser: (user: User | null) => {
          set({ user, isAuthenticated: !!user });
        },
        getToken: () => {
          const { user } = get();
          return user?.token || null;
        },
        getRefreshToken: () => {
          const { user } = get();
          return user?.refreshToken || null;
        },
        isTokenExpired: () => {
          const { user } = get();
          if (!user?.expiresAt) return true;
          return new Date() >= new Date(user.expiresAt);
        },
        isRefreshTokenExpired: () => {
          const { user } = get();
          if (!user?.refreshTokenExpiresAt) return true;
          return new Date() >= new Date(user.refreshTokenExpiresAt);
        },

        clearAuth: () => {
          set({ user: null, isAuthenticated: false });
        },
      }),
      {
        name: STORE_NAME,
        storage: createJSONStorage(() => cookieStorage),
      }
    )
  );
};

export const authStore = createAuthStore();
export const useAuthStore = () => authStore();
